import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Alert, Button, Spinner } from "../../design-system";
import { Icon } from "../../design-system/Icon";
import useInspectionPortal from "./useInspectionPortal";

/**
 * Layout público del portal de Inspección. Header propio (sin sidebar de
 * Kronaly), tabs horizontales y guard que redirige a /inspeccion si no hay
 * token válido. Comparten layout: Dashboard, Empleados, Jornadas, Auditoría,
 * Verificar.
 */

const TABS = [
  { to: "/inspeccion/dashboard", label: "Dashboard", icon: "LayoutDashboard" },
  { to: "/inspeccion/empleados", label: "Empleados", icon: "Users" },
  { to: "/inspeccion/jornadas", label: "Jornadas", icon: "Clock" },
  { to: "/inspeccion/auditoria", label: "Auditoría", icon: "FileSearch" },
  { to: "/inspeccion/verificar", label: "Verificar export", icon: "ShieldCheck" },
];

export default function PortalLayout({ children }) {
  const { workspace, loading, error, logout, token } = useInspectionPortal();
  const location = useLocation();

  if (loading) {
    return (
      <Wrap>
        <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
          <Spinner />
        </div>
      </Wrap>
    );
  }

  if (error) {
    return (
      <Wrap>
        <Alert tone="danger">{error}</Alert>
        <div style={{ marginTop: 12 }}>
          <Button variant="ghost" icon="LogOut" onClick={logout}>
            Salir
          </Button>
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Header workspace={workspace} token={token} onLogout={logout} />
      <Tabs current={location.pathname} />
      <div style={{ marginTop: 16 }}>{children}</div>
    </Wrap>
  );
}

function Wrap({ children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--kr-bg-app, #f6f7f9)",
        padding: "24px 16px 48px",
      }}
    >
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>{children}</div>
    </div>
  );
}

function Header({ workspace, token, onLogout }) {
  return (
    <div
      style={{
        background: "var(--kr-bg, #fff)",
        border: "1px solid var(--kr-border)",
        borderRadius: 8,
        padding: "14px 18px",
        marginBottom: 12,
        display: "flex",
        alignItems: "center",
        gap: 16,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--kr-fg-muted)" }}>
          Portal de Inspección de Trabajo · Art. 34.9 ET
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, color: "var(--kr-fg-strong)", marginTop: 2 }}>
          {workspace?.name || "—"}
        </div>
        {workspace && (
          <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 2 }}>
            {workspace.employees_count_active} empleados activos
            {workspace.employees_count_total > workspace.employees_count_active && (
              <> · {workspace.employees_count_total - workspace.employees_count_active} dados de baja conservados</>
            )}
            {workspace.timezone && <> · {workspace.timezone}</>}
          </div>
        )}
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div style={{ fontSize: 10, color: "var(--kr-fg-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Sesión segura
        </div>
        <div style={{ fontSize: 12, fontFamily: "var(--kr-font-mono)", color: "var(--kr-fg)" }}>
          {token ? `${token.slice(0, 10)}…` : "—"}
        </div>
        <Button variant="ghost" size="sm" icon="LogOut" onClick={onLogout}>
          Cerrar sesión
        </Button>
      </div>
    </div>
  );
}

function Tabs({ current }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 4,
        background: "var(--kr-bg, #fff)",
        border: "1px solid var(--kr-border)",
        borderRadius: 8,
        padding: 4,
        overflowX: "auto",
      }}
    >
      {TABS.map((t) => {
        const active = current.startsWith(t.to);
        return (
          <NavLink
            key={t.to}
            to={t.to}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 500,
              textDecoration: "none",
              background: active ? "var(--kr-primary-50, #eef2ff)" : "transparent",
              color: active ? "var(--kr-primary-700, #4338ca)" : "var(--kr-fg-muted)",
              whiteSpace: "nowrap",
            }}
          >
            <Icon name={t.icon} size={14} />
            {t.label}
          </NavLink>
        );
      })}
    </div>
  );
}
